<script lang="ts" setup>
import { useAppUserDataStore } from "~/composables/useAppUserDataStore";
import { SubmittedDocuments } from "~/types/SubmittedDocument";

const props = defineProps({
  documents: {
    type: Object as PropType<SubmittedDocuments>,
    required: true,
  },
  align: {
    type: String as PropType<"left" | "right" | "center">,
    default: "center",
  },
  allowFilters: {
    type: Boolean,
    default: false,
  },
});

const appUserStore = useAppUserDataStore();
const {
  isPublicDemo,
  hasIEWLMS,
  appUser,
  showFilters,
  levelFilter,
  lessonFilter,
  searchQuery,
  sortKey,
  sortDirection,
} = storeToRefs(appUserStore);

const sortingOptions = computed(() => {
  return [
    {
      label: "Last Updated",
      value: "lastUpdatedTimestamp",
    },
    {
      label: "Submitted Date",
      value: "submittedAtTimestamp",
    },
    {
      label: "Creation Date",
      value: "createOnTimestamp",
    },
  ];
});

watch(sortKey, async () => {
  appUserStore.appUser!.preferredDocumentSortField = sortKey.value;
  if (isPublicDemo.value == true) return;
  await appUserStore.saveUser();
});

watch(sortDirection, async () => {
  appUserStore.appUser!.preferredDocumentSortOrder = sortDirection.value;
  if (isPublicDemo.value == true) return;
  await appUserStore.saveUser();
});

const filteredDocuments = computed(() => {
  const documentsCopy = [...props.documents]; // Create a copy to avoid mutating props

  return documentsCopy.filter((doc) => {
    if (levelFilter.value != null && doc.levelId != levelFilter.value) {
      return false;
    }

    if (lessonFilter.value != null && doc.lessonId != lessonFilter.value) {
      return false;
    }

    if (searchQuery.value != "") {
      const query = searchQuery.value.toLowerCase();
      const title = doc.name;
      const studentName = doc.studentName;
      const classroomName = doc.classroomName;
      const assignmentName = doc.assignmentName;
      const lessonName = doc.lessonName;

      if (
        !title.toLowerCase().includes(query) &&
        !studentName?.toLowerCase().includes(query) &&
        !classroomName?.toLowerCase().includes(query) &&
        !assignmentName?.toLowerCase().includes(query) &&
        !lessonName?.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    return true;
  });
});

const sortedDocument = computed(() => {
  const documentsCopy = [...filteredDocuments.value]; // Create a copy to avoid mutating props

  documentsCopy.sort((a, b) => {
    switch (sortKey.value) {
      case "submittedAtTimestamp":
        if (
          a.submittedAtTimestamp &&
          b.submittedAtTimestamp &&
          a.submittedAtTimestamp.toMillis &&
          b.submittedAtTimestamp.toMillis
        ) {
          return sortDirection.value === "desc"
            ? b.submittedAtTimestamp.toMillis() -
                a.submittedAtTimestamp.toMillis()
            : a.submittedAtTimestamp.toMillis() -
                b.submittedAtTimestamp.toMillis();
        }
      // Fall through if timestamps are not available
      case "createOnTimestamp":
        if (
          a.createOnTimestamp !== undefined &&
          b.createOnTimestamp !== undefined
        ) {
          return sortDirection.value === "desc"
            ? b.createOnTimestamp - a.createOnTimestamp
            : a.createOnTimestamp - b.createOnTimestamp;
        }
      // Fall through if createOnTimestamp is not available
      case "lastUpdatedTimestamp":
      default:
        if (
          a.lastUpdatedTimestamp !== undefined &&
          b.lastUpdatedTimestamp !== undefined
        ) {
          return sortDirection.value === "desc"
            ? b.lastUpdatedTimestamp - a.lastUpdatedTimestamp
            : a.lastUpdatedTimestamp - b.lastUpdatedTimestamp;
        }
        return 0; // If none of the timestamps are available, consider them equal
    }
  });

  return documentsCopy.filter((doc) => {
    const docSubmissionType =
      doc.documentSubmissionType ?? DocumentSubmissionType.finalDraft;

    if (
      hasIEWLMS.value() != true &&
      docSubmissionType != DocumentSubmissionType.finalDraft
    ) {
      return false;
    }

    return true;
  }); // Return the sorted copy
});

const toggleDirection = () => {
  sortDirection.value = sortDirection.value === "desc" ? "asc" : "desc";
};

const showOtherUsersSubmissions = computed(() => {
  return appUser.value?.showOtherUsersSubmissions ?? true;
});
const toggleShowFilters = () => {
  showFilters.value = !showFilters.value;
};

const userDocumentsStore = useUserDocumentsStore();
const {
  homePageUngradedDocuments: ungradedDocuments,
  numGradedDocuments,
  hasOtherUserClassroomSubmissions,
} = storeToRefs(userDocumentsStore);

const onUpdateShowOtherUsersSubmissions = async (value: boolean) => {
  if (appUser.value == undefined) return;

  appUser.value.showOtherUsersSubmissions = value;

  await appUserStore.saveUser();
};
</script>

<template>
  <div class="flex flex-col pb-8 w-full">
    <div
      class="pt-2 pb-4 flex flex-row max-w-[800px] w-full"
      :style="{
        marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
        marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
      }"
    >
      <div class="flex flex-row items-start grow">
        <slot name="actions" />
        <div class="mr-2 grow flex flex-col">
          <BaseSelectInput
            v-model="sortKey"
            :options="sortingOptions"
            :can-clear="false"
          />
          <div v-auto-animate>
            <div v-if="showFilters" class="flex flex-col mt-4">
              <div class="w-full">
                <BaseSearchInput
                  v-model="searchQuery"
                  placeholder="Search"
                  class="mb-4 w-full"
                />
              </div>
              <div
                class="flex flex-row items-center gap-2 w-full max-w-[800px]"
              >
                <div class="flex-1 min-w-0">
                  <LevelsSelect v-model="levelFilter" />
                </div>
                <div class="flex-1 min-w-0">
                  <LessonsSelect v-model="lessonFilter" />
                </div>
              </div>
              <div>
                <div
                  v-if="hasOtherUserClassroomSubmissions"
                  class="flex flex-row items-center pt-4 pb-2"
                >
                  <span> Show other teachers' submissions? </span>
                  <BaseToggle
                    :model-value="showOtherUsersSubmissions"
                    @update:model-value="onUpdateShowOtherUsersSubmissions"
                    class="ml-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="h-[42px] flex flex-row items-center">
          <BaseIconButton @click="toggleDirection" :size="40">
            <BaseIconSortDesc v-if="sortDirection == 'desc'" size="30" />
            <BaseIconSortAsc v-if="sortDirection == 'asc'" size="30" />
          </BaseIconButton>
          <BaseIconButton
            v-if="allowFilters"
            class="ml-2"
            :tooltip="showFilters ? 'Hide Filters' : 'Show Filters'"
            :active="showFilters"
            @click="toggleShowFilters"
            :size="40"
          >
            <icon
              :name="
                showFilters ? 'lucide:file-search-2' : 'lucide:file-search-2'
              "
              size="30"
            />
          </BaseIconButton>
        </div>
      </div>
    </div>
    <div class="flex flex-col grow" v-auto-animate>
      <BaseEmptyListMessage
        v-if="sortedDocument.length == 0"
        class="w-full max-w-[800px]"
        :style="{
          marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
          marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
        }"
      >
        No Documents
      </BaseEmptyListMessage>
      <div
        v-for="document in sortedDocument"
        :key="`${document.id}-${document.state}`"
        class="w-full flex flex-col h-full"
      >
        <DocumentsItem
          :submitted-document="document"
          class="rounded max-w-[800px] w-full border mb-2"
          :style="{
            marginLeft: align === 'right' || align == 'center' ? 'auto' : '0',
            marginRight: align === 'left' || align == 'center' ? 'auto' : '0',
          }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
